import { useNavigate } from "react-router-dom";
import { PillButton } from "./PillButton";
import { MessageCircle } from "lucide-react";

/** Decorative floating bubbles — anonymised feedback snippets, no real names or logos. */
const floatingBubbles = [
  { text: "\u201cOur 1:1s finally changed\u201d", pos: "left-[3%] top-[16%]", tone: "bg-[hsl(24_82%_90%)]", dur: 7, delay: 0 },
  { text: "\u201cI'd never say this in a survey\u201d", pos: "right-[4%] top-[22%]", tone: "bg-[hsl(276_55%_92%)]", dur: 8, delay: 0.6 },
  { text: "\u201cBurnout on the Support team\u201d", pos: "left-[7%] bottom-[20%]", tone: "bg-[hsl(200_70%_92%)]", dur: 9, delay: 1.2 },
  { text: "\u201cFlexible Fridays are working\u201d", pos: "right-[7%] bottom-[16%]", tone: "bg-[hsl(150_45%_90%)]", dur: 7.5, delay: 0.3 },
];



export const HeroChat = () => {
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden">
      <div className="container mx-auto px-3 lg:px-8 pt-4 sm:pt-6">
        <div className="relative rounded-[1.75rem] sm:rounded-[2rem] overflow-hidden px-5 sm:px-10 lg:px-16 py-16 sm:py-28 lg:py-36 bg-background">
          {/* Soft pastel auras */}
          <div className="absolute -right-32 -top-24 w-[560px] h-[560px] rounded-full bg-[hsl(24_82%_88%)] opacity-70 blur-[110px] pointer-events-none" />
          <div className="absolute -left-40 bottom-[-120px] w-[520px] h-[520px] rounded-full bg-[hsl(276_55%_90%)] opacity-70 blur-[110px] pointer-events-none" />
          <div className="absolute left-1/3 top-1/2 w-[380px] h-[380px] rounded-full bg-[hsl(200_70%_90%)] opacity-50 blur-[120px] pointer-events-none" />

          {/* Floating anonymous feedback bubbles (decorative, desktop only) */}
          <div className="hidden lg:block pointer-events-none select-none" aria-hidden>
            {floatingBubbles.map((b) => (
              <div
                key={b.text}
                className={`absolute ${b.pos} flex items-center gap-2.5 rounded-full bg-card/90 backdrop-blur border border-border pl-2 pr-4 py-2 shadow-card`}
                style={{ animation: `bubble-float ${b.dur}s ease-in-out ${b.delay}s infinite` }}
              >
                <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full ${b.tone} text-foreground/60`}>
                  <MessageCircle className="h-3.5 w-3.5" />
                </span>
                <span className="text-[12.5px] text-foreground/75 whitespace-nowrap">{b.text}</span>
              </div>
            ))}
          </div>



          {/* Eyebrow */}
          <div className="relative flex justify-center mb-6 sm:mb-8">
            <span className="inline-flex items-center gap-2 rounded-full bg-card/80 backdrop-blur-md border border-border px-3.5 py-1.5 text-[11.5px] sm:text-xs font-medium text-muted-foreground tracking-wide">
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              Anonymous by design · GDPR ready
            </span>
          </div>

          <div className="relative max-w-4xl mx-auto text-center text-foreground">
            <h1 className="text-[34px] sm:text-6xl lg:text-[76px] leading-[1.05] tracking-[-0.02em]">
              The anonymous feedback tool{" "}
              <span className="font-display">HR actually trusts</span>
            </h1>
            <p className="mt-6 sm:mt-8 text-base sm:text-lg lg:text-xl text-muted-foreground font-normal leading-relaxed max-w-2xl mx-auto">
              Finally get the honest truth about culture, retention, and morale — by simply chatting to your AI advisor. Live in 5 minutes.
            </p>

            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2.5 sm:gap-3">
              <PillButton variant="primary" size="lg" className="w-full sm:w-auto" onClick={() => navigate("/auth?mode=signup")}>
                Create anonymous page
              </PillButton>
              <PillButton variant="outline" size="lg" className="w-full sm:w-auto" onClick={() => navigate("/contact")}>
                Book a demo
              </PillButton>
            </div>
            <div className="mt-6 grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11.5px] sm:text-xs text-muted-foreground uppercase tracking-[0.14em]">
              <span>Free 15-day trial</span>
              <span className="hidden sm:inline text-border" aria-hidden>·</span>
              <span>No credit card</span>
              <span className="hidden sm:inline text-border" aria-hidden>·</span>
              <span>GDPR compliant</span>
              <span className="hidden sm:inline text-border" aria-hidden>·</span>
              <span>Live in 5 minutes</span>
            </div>
          </div>
        </div>
      </div>
    </section>

  );
};
